// Screen 2 — "Миний буцаалтууд" hub list (screens-buyer-app §2).
//
// Thinking-in-React: a presentational, props-only component. Rows come from the
// caller's SWR data; this renders responsive cards (mobile) + a table (desktop),
// each row links to detail, and PROPOSED rows expose a quick [Зөвшөөрөх] +
// an "action required" indicator (icon, never an emoji). The accept mutation is
// owned by the parent (passed as onAccept) so this stays a static render.
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Group,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { BellRingingIcon, CaretRightIcon, CheckIcon, EyeIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { IReturnSummary } from "../../../apis/return";
import { currencyFormat } from "../../../utils";
import { dateFormat, dateTimeSecFormat } from "../../../utils/date";
import { subReasonLabel } from "../../return/atoms";
import { ReturnStatusBadge } from "../../return/atoms/status-badge";
import { ReasonTooltip } from "../../ui";
import RenderText from "../../ui/render-text";
import { reasonLabelMn, BUYER_RETURNS_ROUTES, TNUM } from "./constants";

export interface BuyerReturnsListProps {
  rows: IReturnSummary[];
  page: number;
  totalPages: number;
  total?: number;
  limit?: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  /** RETURN_ACCEPT grant — gates the quick [Зөвшөөрөх] on PROPOSED rows. */
  canAccept: boolean;
  /** Quick-accept (PROPOSED → ACCEPTED). Parent runs the mutation (idem+If-Match). */
  onAccept?: (r: IReturnSummary) => void;
  /** returnRequestId currently being accepted (button loading state). */
  acceptingId?: string | null;
  /** Empty-state slot rendered when rows is empty. */
  emptyState?: React.ReactNode;
}

// Order number shown on a row (buyer side: orderNumber from the summary).
function orderLabel(r: IReturnSummary): string {
  return r.orderNumber ?? "-";
}

// B69: a missing amount is "-", never "0₮".
const amountOrDash = (v?: number | null): string => (v == null ? "-" : currencyFormat(v));

// PROPOSED rows require the buyer to act.
function isActionRequired(r: IReturnSummary): boolean {
  return r.status === "PROPOSED";
}

// ── Mobile card ──────────────────────────────────────────────────────────────
function BuyerReturnCard({
  r,
  canAccept,
  onAccept,
  acceptingId,
}: {
  r: IReturnSummary;
  canAccept: boolean;
  onAccept?: (r: IReturnSummary) => void;
  acceptingId?: string | null;
}) {
  const { t } = useTranslation("buyerApp");
  const detailTo = BUYER_RETURNS_ROUTES.detail(r.returnRequestId);
  const actionRequired = isActionRequired(r);
  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Anchor component={Link} to={detailTo} fw={700} size="sm" style={TNUM}>
            {r.returnNumber}
          </Anchor>
          <Group gap={6} wrap="nowrap">
            {actionRequired ? (
              <Tooltip label={t("list.actionRequired")} withArrow>
                <ThemeIcon size={20} radius="xl" variant="light" color="orange" aria-label={t("list.actionRequired")}>
                  <BellRingingIcon size={12} weight="fill" />
                </ThemeIcon>
              </Tooltip>
            ) : null}
            <ReturnStatusBadge status={r.status} />
          </Group>
        </Group>

        <Text size="sm" c="dimmed" style={TNUM}>
          {dateTimeSecFormat(r.requestedAt)}
          {r.orderNumber ? ` · ${t("list.fromOrder", { orderNumber: r.orderNumber })}` : ""}
        </Text>

        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={600} style={TNUM}>
            {amountOrDash(r.totalRequestedAmount)}
          </Text>
          <Text size="sm" c="dimmed">
            · {reasonLabelMn(r.returnReason)}
          </Text>
        </Group>
        {r.subReason ? (
          <Text size="sm" c="dimmed">
            {t("list.subReasonPrefix", { value: subReasonLabel(r.subReason) })}
          </Text>
        ) : null}

        <Group justify="flex-end" gap="sm" mt={4}>
          {actionRequired && canAccept && onAccept ? (
            <Button
              size="sm"
              variant="filled"
              color="green"
              loading={acceptingId === r.returnRequestId}
              onClick={() => onAccept(r)}
            >
              {t("list.approve")}
            </Button>
          ) : null}
          <Button size="sm" variant="light" component={Link} to={detailTo} rightSection={<CaretRightIcon size={14} />}>
            {t("list.detail")}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

// ── Desktop table ─────────────────────────────────────────────────────────────
// O31: «Үйлдэл» is the first column, quiet gray ActionIcons. A PROPOSED row's
// accept stays visible without RETURN_ACCEPT but aria-disabled with the reason.
function BuyerReturnsTable({
  rows,
  canAccept,
  onAccept,
  acceptingId,
}: {
  rows: IReturnSummary[];
  canAccept: boolean;
  onAccept?: (r: IReturnSummary) => void;
  acceptingId?: string | null;
}) {
  const { t } = useTranslation("buyerApp");
  return (
    <Table.ScrollContainer minWidth={860}>
      <Table striped highlightOnHover withTableBorder verticalSpacing="sm" horizontalSpacing="md"
        styles={{ td: { verticalAlign: "middle" } }}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={110}>{t("list.colActions")}</Table.Th>
            <Table.Th w={170}>{t("list.colRetNumber")}</Table.Th>
            <Table.Th w={140}>{t("list.colOrder")}</Table.Th>
            <Table.Th>{t("list.colReason")}</Table.Th>
            <Table.Th w={140} style={{ textAlign: "right" }}>{t("list.colAmount")}</Table.Th>
            <Table.Th w={190}>{t("list.colStatus")}</Table.Th>
            <Table.Th w={140}>{t("list.colDate")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => {
            const detailTo = BUYER_RETURNS_ROUTES.detail(r.returnRequestId);
            const actionRequired = isActionRequired(r);
            return (
              <Table.Tr key={r.returnRequestId}>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    {actionRequired && onAccept ? (
                      <ReasonTooltip reason={canAccept ? undefined : t("list.needPermission", { code: "RETURN_ACCEPT" })}>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={t("list.approve")}
                          loading={acceptingId === r.returnRequestId}
                          onClick={() => onAccept(r)}
                        >
                          <CheckIcon size={18} />
                        </ActionIcon>
                      </ReasonTooltip>
                    ) : null}
                    <Tooltip label={t("list.detail")} withArrow>
                      <ActionIcon component={Link} to={detailTo} variant="subtle" color="gray" aria-label={t("list.detail")}>
                        <EyeIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Anchor component={Link} to={detailTo} fw={600} ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text)" style={{ ...TNUM, whiteSpace: "nowrap" }}>
                    {r.returnNumber}
                  </Anchor>
                </Table.Td>
                <Table.Td>
                  <RenderText text={orderLabel(r)} style={TNUM} />
                </Table.Td>
                <Table.Td>
                  <RenderText text={reasonLabelMn(r.returnReason)} fontWeight={400} />
                  {r.subReason ? (
                    <RenderText
                      text={t("list.subReasonPrefix", { value: subReasonLabel(r.subReason) })}
                      color="var(--mantine-color-dimmed)"
                      fontWeight={400}
                    />
                  ) : null}
                </Table.Td>
                <Table.Td style={{ textAlign: "right" }}>
                  <RenderText text={amountOrDash(r.totalRequestedAmount)} fontWeight={600} style={TNUM} ml="auto" />
                </Table.Td>
                <Table.Td>
                  <ReturnStatusBadge status={r.status} />
                  {actionRequired ? (
                    <RenderText text={t("list.actionRequired")} color="var(--mantine-color-dimmed)" fontWeight={400} />
                  ) : null}
                </Table.Td>
                <Table.Td>
                  <RenderText text={dateFormat(r.requestedAt)} fontWeight={400} style={TNUM} />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

export function BuyerReturnsList({
  rows,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  canAccept,
  onAccept,
  acceptingId,
  emptyState,
}: BuyerReturnsListProps) {
  const { t } = useTranslation("buyerApp");
  if (!rows.length) {
    return <>{emptyState ?? <Box p="xl" ta="center"><Text c="dimmed">{t("list.notFound")}</Text></Box>}</>;
  }

  const effectiveLimit = limit ?? rows.length;
  const rangeStart = total != null ? (page - 1) * effectiveLimit + 1 : 0;
  const rangeEnd = total != null ? Math.min(page * effectiveLimit, total) : 0;
  const showFooter = totalPages > 1 || total != null;

  return (
    <Stack gap="md">
      {/* Mobile: stacked cards */}
      <Stack gap="sm" hiddenFrom="md">
        {rows.map((r) => (
          <BuyerReturnCard
            key={r.returnRequestId}
            r={r}
            canAccept={canAccept}
            onAccept={onAccept}
            acceptingId={acceptingId}
          />
        ))}
      </Stack>

      {/* Desktop: table */}
      <Box visibleFrom="md">
        <BuyerReturnsTable rows={rows} canAccept={canAccept} onAccept={onAccept} acceptingId={acceptingId} />
      </Box>

      {showFooter ? (
        <Group justify="space-between" align="center">
          {total != null ? (
            <Text size="sm" c="dimmed" style={TNUM}>
              {rangeStart}-{rangeEnd} / {total}
            </Text>
          ) : <Box />}
          {totalPages > 1 ? (
            <Pagination value={page} total={totalPages} onChange={onPageChange} size="sm" radius="md" />
          ) : null}
        </Group>
      ) : null}
    </Stack>
  );
}
